import { forwardRef, MouseEvent, useEffect } from "react";
import { useKeysPress } from "../../hooks/useKeysPress/index.js";
import { usePortal } from "../../hooks/usePortal/index.js";
import { CloseLinear } from "../Iconsax/CloseLinear.js";
import { CloseButton, ModalBody, ModalContent, ModalOverlay } from "./ModalV2.style.js";
import { ModalV2Props } from "./ModalV2.types.js";

export const ModalV2 = forwardRef<HTMLDivElement, ModalV2Props>((props, ref): JSX.Element | null => {
    const {
        isOpen,
        onClose,
        onOpen,
        closeButton = false,
        children,
        closeOnEscape = true,
        mountNode,
        isBodyFullWidth = false,
        noPadding = false,
        noSizeConstraint = false,
        fullscreen = false,
        "data-testid": dataTestId,
        preventClosingSidepanel,
        elevation,
    } = props;

    useEffect(() => {
        if (isOpen) {
            onOpen?.();
        }
    }, [isOpen]);

    const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose?.();
        }
    };

    useKeysPress({
        sequences: [
            {
                handler: (_, event) => {
                    if (closeOnEscape) {
                        onClose?.();
                        event.preventDefault();
                        event.stopPropagation();
                    }
                },
                keys: ["escape"],
            },
        ],
    });

    const Portal = usePortal({
        mountNode: mountNode || document.body,
    });

    if (!isOpen) {
        return null;
    }

    return (
        <Portal>
            <ModalOverlay
                ref={ref}
                elevation={elevation}
                onMouseDown={handleOverlayClick}
                {...(preventClosingSidepanel ? { className: "modal-container" } : {})}
            >
                <ModalContent data-testid={dataTestId} noPadding={noPadding} noSizeConstraint={noSizeConstraint} fullscreen={fullscreen}>
                    {closeButton && (
                        <CloseButton
                            data-testid="modal-close-button"
                            variant="tinted"
                            color="neutral"
                            size="small"
                            icon={CloseLinear}
                            onClick={onClose}
                        />
                    )}
                    <ModalBody width={isBodyFullWidth ? "100%" : "auto"}>{children}</ModalBody>
                </ModalContent>
            </ModalOverlay>
        </Portal>
    );
});
